import { useEffect } from 'react';
import type { CalculatorBootstrap, CalculatorConfig, ControlStyle, FieldValues, IrsLimitsSet, StepChangeEvent } from '../types';
import { DEFAULT_CONTROL_STYLE } from '../types';
import { CalculatorProvider, useCalculator } from '../state/CalculatorProvider';
import { pushStepChangeToDataLayer } from '../lib/analytics';
import { IntroScreen } from './IntroScreen';
import { StepPanel } from './StepPanel';
import { ResultsFrame } from './ResultsFrame';
import { ScreenTransition } from './ScreenTransition';

function CalculatorBody<TValues extends FieldValues, TResult>() {
  const { config, state, derived, controlStyle } = useCalculator<TValues, TResult>();

  useEffect(() => {
    const evt: StepChangeEvent = {
      calculatorId: config.id,
      stepId: state.phase === 'results' ? 'results' : (derived.currentStep?.id ?? state.phase),
      stepIndex: state.phase === 'steps' ? state.stepIndex : derived.visibleSteps.length,
      stepCount: derived.visibleSteps.length + 1,
    };
    (config.onStepChange ?? pushStepChangeToDataLayer)(evt);
  }, [config, state.phase, state.stepIndex, derived.currentStep, derived.visibleSteps.length]);

  const placement = config.resultsPlacement ?? 'terminal';
  const showSteps = state.phase !== 'intro' && (state.phase !== 'results' || placement === 'always');
  const showResults = placement === 'always' || state.phase === 'results';

  const resultsFirst = placement === 'always' && config.resultsOrder === 'before';

  const screenKey =
    state.phase === 'intro'
      ? 'intro'
      : state.phase === 'results'
        ? 'results'
        : `step:${derived.currentStep?.id ?? state.stepIndex}`;
  const screenOrder = state.phase === 'intro' ? -1 : state.phase === 'results' ? Number.MAX_SAFE_INTEGER : state.stepIndex;

  return (
    <div
      className="hsab-calc"
      data-calculator={config.id}
      data-layout={config.layout ?? 'legacy'}
      data-shape={controlStyle.shape}
      data-size={controlStyle.size}
      data-animation={controlStyle.animation}
    >
      {placement === 'terminal' ? (
        // Wizard calculators: exactly one screen at a time, animated per the
        // Animation setting (ScreenTransition.tsx).
        <ScreenTransition screenKey={screenKey} order={screenOrder} animation={controlStyle.animation}>
          {state.phase === 'intro' ? <IntroScreen /> : state.phase === 'results' ? <ResultsFrame /> : <StepPanel />}
        </ScreenTransition>
      ) : (
        <>
          {state.phase === 'intro' && <IntroScreen />}
          {resultsFirst && showResults && <ResultsFrame />}
          {showSteps && <StepPanel />}
          {!resultsFirst && showResults && <ResultsFrame />}
        </>
      )}
    </div>
  );
}

export function CalculatorApp<TValues extends FieldValues, TResult>({
  config,
  limits,
  options,
  controlStyle,
}: {
  config: CalculatorConfig<TValues, TResult>;
  limits: IrsLimitsSet;
  options: CalculatorBootstrap['options'];
  /** Defaults to today's rendering (docs/calculator-control-style.md §4.4) so
   *  the dev harness and any test rendering <CalculatorApp> with just the
   *  other three props keeps compiling and keeps rendering today's look. */
  controlStyle?: ControlStyle;
}) {
  return (
    <CalculatorProvider
      config={config}
      limits={limits}
      options={options}
      controlStyle={controlStyle ?? DEFAULT_CONTROL_STYLE}
    >
      <CalculatorBody<TValues, TResult> />
    </CalculatorProvider>
  );
}
